import { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // Header
    'header.about': 'About Us',
    'header.pillars': 'Pillars',
    'header.partners': 'Our Partners',
    'header.procurement': 'Procurement',
    'header.careers': 'Careers',
    'header.contact': 'Contact',
    'header.getInTouch': 'Get in Touch',
    
    // Hero
    'hero.title': 'Delivering fully integrated hospitality experiences.',
    'hero.marketShare': '32% Market Share in Egypt',
    'hero.cities': '5+ Cities Across MENA',
    'hero.technology': '100% Proprietary Technology',
    
    // About
    'about.title': 'About BHG',
    'about.description': 'Brassbell Hospitality Group (BHG) is a multi-faceted hospitality company with four core divisions, offering a diverse portfolio that ranges from contemporary serviced apartments to thoughtfully designed boutique hotels. With a commitment to innovation, we integrate tech-driven property management solutions that enhance operational efficiency and elevate the guest experience. Backed by strong investment partners, BHG is rapidly expanding across Egypt and the MENA region. Our mission is to drive growth and innovation in hospitality while creating lasting value for stakeholders, and our vision is to become the region\'s leading name in luxury, trust, and unforgettable experiences.',
    
    // Pillars
    'pillars.title': 'Our Portfolio',
    'pillars.homes': 'Homes',
    'pillars.hotels': 'Hotels',
    'pillars.developments': 'Developments',
    'pillars.technologies': 'Technologies',
    'pillars.homes.desc': 'Premium serviced apartments',
    'pillars.hotels.desc': 'Boutique hospitality experiences',
    'pillars.developments.desc': 'Real estate development projects',
    'pillars.technologies.desc': 'PropTech solutions',
    
    // Partners
    'partners.title': 'Our Partners',
    'partners.subtitle': 'Trusted by leading developers, creatives, and operators across the MENA region',
    'partners.description': 'At Brassbell Hospitality Group, we are proud to collaborate with visionary partners across real estate, design, hospitality, and beyond. From development groups to lifestyle operators and creative collaborators, our trusted partnerships allow us to deliver exceptional serviced living experiences. Together, we blend world-class hospitality standards with local expertise to create lasting value for our clients, communities, and guests.',
    
    // Contact
    'contact.title': 'Contact Us',
    'contact.getInTouch': 'Get in Touch',
    'contact.name': 'Name',
    'contact.email': 'Email',
    'contact.message': 'Message',
    'contact.send': 'Send Message',
    'contact.address': 'Address',
    'contact.phone': 'Phone',
    'contact.investment.title': 'Investment Opportunities',
    'contact.investment.description': 'Partner with us on new developments and expansion projects',
    'contact.investment.cta': 'Explore Partnerships',
    'contact.business.title': 'Business Development',
    'contact.business.description': 'Management contracts and strategic collaborations',
    'contact.business.cta': 'Start Conversation',
    'contact.technology.title': 'Technology Solutions',
    'contact.technology.description': 'Learn about our PMS and technology services',
    'contact.technology.cta': 'Tech Inquiry',
    'contact.offices': 'Our Offices',
    'contact.headquarters': 'Headquarters',
    'contact.ready.title': 'Ready to Work Together?',
    'contact.ready.description': 'Whether you\'re an investor, partner, or looking for our services, we\'re excited to explore how we can create value together.',
    'contact.schedule': 'Schedule a Meeting',
    'contact.download': 'Download Investor Deck',
    'contact.description': 'Ready to explore partnerships, discuss opportunities, or learn more about our technology solutions? We\'re here to help you succeed.',
    'contact.cairo': 'Cairo',
    'contact.egypt': 'Egypt',
    'contact.newCairo': 'New Cairo Business District',
    
    // Footer
    'footer.allRightsReserved': 'All rights reserved.',
    'footer.about': 'About BHG',
    'footer.portfolio': 'Portfolio',
    'footer.services': 'Services',
    'footer.contact': 'Contact',
    'footer.quickLinks': 'Quick Links',
    'footer.ourServices': 'Our Services',
    'footer.hotelDevelopment': 'Hotel Development',
    'footer.propertyManagement': 'Property Management',
    'footer.servicedApartments': 'Serviced Apartments',
    'footer.technologySolutions': 'Technology Solutions',
    'footer.boutiqueHotels': 'Boutique Hotels',
    'footer.ourPresence': 'Our Presence',
    'footer.description': 'Leading hospitality group operating across Egypt and the region, pioneering boutique accommodations and proprietary technology solutions.',
    'footer.headquarters': 'Headquarters: Zamalek, Egypt',
    'footer.marketShare': '32% Market Share | 500+ Units | 5+ Cities',
    'footer.locations.cairo': 'Cairo, Egypt',
    'footer.locations.alexandria': 'Alexandria, Egypt',
    'footer.locations.northCoast': 'North Coast, Egypt',
    'footer.locations.hurghada': 'Hurghada, Egypt',
    'footer.locations.jeddah': 'Jeddah, Saudi Arabia',
    
    // Careers
    'careers.title': 'Join Our Team',
    'careers.subtitle': 'Build your career with Brassbell Hospitality Group',
    'careers.description': 'We are always looking for talented individuals who share our passion for excellence in hospitality. Join us in shaping the future of hospitality across the MENA region.',
    'careers.apply': 'Apply Now',
    'careers.viewPosition': 'View Position',
    'careers.noOpenings': 'No current openings',
    'careers.form.name': 'Full Name',
    'careers.form.email': 'Email Address',
    'careers.form.phone': 'Phone Number',
    'careers.form.position': 'Position Applied For',
    'careers.form.experience': 'Years of Experience',
    'careers.form.cover': 'Cover Letter',
    'careers.form.resume': 'Upload Resume',
    'careers.form.submit': 'Submit Application',
    
    // Procurement
    'procurement.title': 'Procurement',
    'procurement.subtitle': 'Partner with us for excellence',
    'procurement.description': 'We are committed to building strong partnerships with suppliers who share our values of quality, reliability, and innovation.',
    'procurement.register': 'Register as Supplier',
    'procurement.requirements': 'Supplier Requirements',
    'procurement.process': 'Procurement Process',
    'procurement.contact': 'Procurement Contact',
    
    // Homes
    'homes.title': 'Serviced Apartments',
    'homes.subtitle': 'Premium living experiences',
    'homes.description': 'Our serviced apartments combine the comfort of home with the luxury of hotel services, offering flexible living solutions for extended stays.',
    'homes.features': 'Features',
    'homes.locations': 'Locations',
    'homes.bookNow': 'Book Now',
    
    // Hotels
    'hotels.title': 'Boutique Hotels',
    'hotels.subtitle': 'Exceptional hospitality experiences',
    'hotels.description': 'Our boutique hotels offer unique, locally-inspired experiences with world-class amenities and personalized service.',
    'hotels.amenities': 'Amenities',
    'hotels.rooms': 'Rooms & Suites',
    'hotels.dining': 'Dining',
    'hotels.bookNow': 'Book Now',
    
    // Developments
    'developments.title': 'Real Estate Development',
    'developments.subtitle': 'Building the future of hospitality',
    'developments.description': 'Our development projects create innovative spaces that redefine hospitality and residential living across the MENA region.',
    'developments.projects': 'Current Projects',
    'developments.completed': 'Completed Projects',
    'developments.planned': 'Planned Projects',
    
    // Technologies
    'technologies.title': 'PropTech Solutions',
    'technologies.subtitle': 'Innovation in hospitality technology',
    'technologies.description': 'We develop cutting-edge property management and guest experience technologies that drive efficiency and enhance satisfaction.',
    'technologies.solutions': 'Our Solutions',
    'technologies.features': 'Key Features',
    'technologies.integration': 'System Integration',
    
    // Job Application Form
    'job.title': 'Submit Job Application',
    'job.description': 'We\'re always looking for talented individuals to join our team. Please fill out the form below.',
    'job.firstName': 'First Name',
    'job.lastName': 'Last Name',
    'job.email': 'Email Address',
    'job.phone': 'Phone Number',
    'job.position': 'Position of Interest',
    'job.experience': 'Years of Experience',
    'job.resume': 'Resume/CV',
    'job.coverLetter': 'Cover Letter',
    'job.cancel': 'Cancel',
    'job.submit': 'Submit Application',
    'job.submitting': 'Submitting...',
    'job.placeholder.firstName': 'Enter your first name',
    'job.placeholder.lastName': 'Enter your last name',
    'job.placeholder.email': 'Enter your email address',
    'job.placeholder.phone': 'Enter your phone number',
    'job.placeholder.position': 'Select a position or department',
    'job.placeholder.experience': 'Select your experience level',
    'job.placeholder.coverLetter': 'Tell us why you\'d like to join our team...',
    'job.positions.operations': 'Operations',
    'job.positions.revenue': 'Revenue Management',
    'job.positions.guestServices': 'Guest Services',
    'job.positions.housekeeping': 'Housekeeping',
    'job.positions.maintenance': 'Maintenance',
    'job.positions.foodBeverage': 'Food & Beverage',
    'job.positions.management': 'Management',
    'job.positions.other': 'Other',
    'job.experience.0-1': '0-1 years',
    'job.experience.2-5': '2-5 years',
    'job.experience.6-10': '6-10 years',
    'job.experience.10+': '10+ years',
    'job.fileFormats': 'Accepted formats: PDF, DOC, DOCX (max 5MB)',
    'job.errors.firstName': 'First name is required',
    'job.errors.lastName': 'Last name is required',
    'job.errors.email': 'Email is required',
    'job.errors.emailInvalid': 'Please enter a valid email address',
    'job.errors.resume': 'Please upload your resume',
    'job.errors.experience': 'Please select your experience level',
    'job.success': 'Application submitted successfully! We\'ll be in touch soon.',
    'job.error': 'Failed to submit application. Please try again.',
    'common.viewAll': 'View All',
    'common.backToHome': 'Back to Home',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.close': 'Close',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.download': 'Download',
    'common.upload': 'Upload',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.sort': 'Sort',
    
    // FAQ
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Find answers to common questions about our services and partnerships.',
    'faq.pageTitle': 'Frequently Asked Questions',
    'faq.pageSubtitle': 'Everything you need to know about Brassbell Hospitality Group',
    'faq.viewMore': 'View more',
    'faq.searchPlaceholder': 'Search frequently asked questions...',
    'faq.noResults': 'No questions found matching your search.',
    'faq.categories.title': 'Categories',
    'faq.categories.all': 'All Questions',
    'faq.categories.general': 'General',
    'faq.categories.services': 'Services',
    'faq.categories.partnership': 'Partnership',
    'faq.categories.technology': 'Technology',
    
    'faq.q1': 'What is Brassbell Hospitality Group (BHG)?',
    'faq.a1': 'BHG is a fully integrated hospitality and real estate platform that develops, operates, and invests in transformative destinations. We combine brand creation, hotel operations, and real estate development under one umbrella — making us both a House of Brands and a strategic partner for investors and developers.',
    
    'faq.q2': 'What does "House of Brands" mean for BHG?',
    'faq.a2': 'It means that we are not a single-brand operator. Instead, we curate and scale a portfolio of proprietary hospitality IPs — each designed for different market segments, from lifestyle luxury to wellness-driven resorts, to heritage-led boutique concepts. This gives us flexibility to match the right brand to the right asset.',
    
    'faq.q3': 'How does BHG create value for investors and landlords?',
    'faq.a3': 'We apply an investment arbitrage model: Acquire or lease undervalued real estate (often heritage or underperforming assets). Force appreciation through design-led redevelopment and operational repositioning. Operate under strong proprietary brands that command higher ADRs and occupancy. Deliver hotel-level returns on real estate previously valued as residential or commercial stock. This cycle allows us to unlock hidden value and deliver superior returns.',
    
    'faq.q4': 'Does BHG focus on new developments or existing buildings?',
    'faq.a4': 'Our expertise lies in redevelopment and adaptive reuse. We specialize in reimagining existing or heritage assets into high-performing hospitality destinations, rather than ground-up construction. This reduces environmental impact, accelerates time-to-market, and preserves urban character.',
    
    'faq.q5': 'What is Brassbell Developments?',
    'faq.a5': 'Brassbell Developments is our development and investment arm, focusing on identifying, acquiring, and redeveloping real estate assets. Our methodology includes: Selective acquisition of underperforming assets with strong fundamentals. Comprehensive due diligence and feasibility analysis. Adaptive redevelopment with best-in-class architects, contractors, and consultants. Long-term alignment with Brassbell Hotels to ensure operational success.',
    
    'faq.q6': 'What is Brassbell Hotels?',
    'faq.a6': 'Brassbell Hotels is our hotel operations and concept creation division. We operate our proprietary brands, manage white-label hotels, and provide technical services such as: Concept curation & brand creation, Pre-opening & operational setup, Staffing, training, and service standards, Technology integration (PMS, channel management, direct booking), Ongoing hotel management and asset optimization.',
    
    'faq.q7': 'Does BHG provide white-label hotel management?',
    'faq.a7': 'Yes. In addition to our proprietary brands, we provide white-label operations for owners seeking expert management without external branding. Our in-house technical consultancy also helps tailor new brands specifically for individual assets.',
    
    'faq.q8': 'How does BHG ensure governance and transparency?',
    'faq.a8': 'We operate under institutional-grade governance structures, including: Structured lease or revenue-share agreements, Joint steering committees with partners, Transparent financial reporting and performance dashboards, Independent audits and compliance with local and international standards. This ensures alignment between all stakeholders.',
    
    'faq.q9': 'Where is BHG currently expanding?',
    'faq.a9': 'Our growth strategy focuses on Egypt, Saudi Arabia, and the wider MENA region, with select international expansion in gateway cities. We pursue opportunities across urban hotels, lifestyle resorts, and heritage redevelopments.',
    
    'faq.q10': 'What types of partners does BHG work with?',
    'faq.a10': 'We collaborate with: Real estate developers & landlords (for redevelopment and long-term operation), Investment funds & institutional partners (for structured deals and co-investment), Design houses, architects, and consultants (for world-class redevelopment), F&B operators, co-working brands, and lifestyle partners (to enhance programming), Suppliers and service providers (to deliver best-in-class operations).',
    
    'faq.q11': 'How can developers or landlords work with BHG?',
    'faq.a11': 'Partnership structures include: Long-term lease agreements with minimum guarantees, Revenue-share models tied to operational performance, Joint ventures / co-investment structures for larger projects, White-label or brand licensing agreements depending on the asset profile.',
    
    'faq.q12': 'How do I connect with BHG?',
    'faq.a12': 'For partnerships: partnerships@brassbellhospitality.com, For media: media@brassbellhospitality.com, For careers: Visit our Careers Page.',
    
    // Footer
    'footer.company': 'Company',
    
    // 404 Page
    'notFound.title': '404',
    'notFound.message': 'Oops! Page not found',
    'notFound.back': 'Return to Home',
  },
  ar: {
    // Header  
    'header.about': 'نبذة عنا',
    'header.pillars': 'الأعمدة',
    'header.partners': 'شركاؤنا',
    'header.procurement': 'المشتريات',
    'header.careers': 'الوظائف',
    'header.contact': 'اتصل بنا',
    'header.getInTouch': 'تواصل معنا',
    
    // Hero
    'hero.title': 'تقديم تجارب ضيافة متكاملة بالكامل.',
    'hero.marketShare': '32% حصة السوق في مصر',
    'hero.cities': '5+ مدن عبر منطقة الشرق الأوسط وشمال أفريقيا',
    'hero.technology': '100% تكنولوجيا مملوكة',
    
    // About
    'about.title': 'نبذة عن BHG',
    'about.description': 'مجموعة براسبيل للضيافة (BHG) هي شركة ضيافة متعددة الجوانب تضم أربعة أقسام أساسية، وتقدم محفظة متنوعة تتراوح من الشقق المخدومة المعاصرة إلى الفنادق البوتيكية المصممة بعناية. مع التزامنا بالابتكار، ندمج حلول إدارة العقارات المدفوعة بالتكنولوجيا التي تعزز الكفاءة التشغيلية وترفع من تجربة الضيوف. بدعم من شركاء استثمار أقوياء، تتوسع BHG بسرعة عبر مصر ومنطقة الشرق الأوسط وشمال أفريقيا. مهمتنا هي قيادة النمو والابتكار في الضيافة مع خلق قيمة دائمة لأصحاب المصلحة، ورؤيتنا هي أن نصبح الاسم الرائد في المنطقة في الفخامة والثقة والتجارب التي لا تُنسى.',
    
    // Pillars
    'pillars.title': 'محفظتنا',
    'pillars.homes': 'المنازل',
    'pillars.hotels': 'الفنادق',
    'pillars.developments': 'التطوير',
    'pillars.technologies': 'التقنيات',
    'pillars.homes.desc': 'شقق مخدومة فاخرة',
    'pillars.hotels.desc': 'تجارب ضيافة بوتيكية',
    'pillars.developments.desc': 'مشاريع التطوير العقاري',
    'pillars.technologies.desc': 'حلول التكنولوجيا العقارية',
    
    // Partners
    'partners.title': 'شركاؤنا',
    'partners.subtitle': 'موثوق بها من قبل المطورين الرائدين وعلامات الضيافة التجارية في جميع أنحاء منطقة الشرق الأوسط وشمال أفريقيا',
    'partners.description': 'في مجموعة براسبيل للضيافة، نحن فخورون بالتعاون مع المطورين الرائدين ومجموعات العقارات وعلامات الضيافة التجارية في جميع أنحاء منطقة الشرق الأوسط وشمال أفريقيا. تتيح لنا شراكاتنا الموثوقة تقديم تجارب معيشة مخدومة استثنائية، تجمع بين معايير الضيافة ذات المستوى العالمي والخبرة المحلية القوية. معًا، نخلق قيمة دائمة لعملائنا ومجتمعاتنا وضيوفنا.',
    
    // Contact
    'contact.title': 'اتصل بنا',
    'contact.getInTouch': 'تواصل معنا',
    'contact.name': 'الاسم',
    'contact.email': 'البريد الإلكتروني',
    'contact.message': 'الرسالة',
    'contact.send': 'إرسال الرسالة',
    'contact.address': 'العنوان',
    'contact.phone': 'الهاتف',
    'contact.investment.title': 'الفرص الاستثمارية',
    'contact.investment.description': 'شاركنا في مشاريع التطوير الجديدة والتوسع',
    'contact.investment.cta': 'استكشف الشراكات',
    'contact.business.title': 'تطوير الأعمال',
    'contact.business.description': 'عقود الإدارة والتعاون الاستراتيجي',
    'contact.business.cta': 'ابدأ المحادثة',
    'contact.technology.title': 'الحلول التقنية',
    'contact.technology.description': 'تعرف على نظام إدارة العقارات والخدمات التقنية',
    'contact.technology.cta': 'استفسار تقني',
    'contact.offices': 'مكاتبنا',
    'contact.headquarters': 'المقر الرئيسي',
    'contact.ready.title': 'مستعد للعمل معاً؟',
    'contact.ready.description': 'سواء كنت مستثمراً أو شريكاً أو تبحث عن خدماتنا، نحن متحمسون لاستكشاف كيف يمكننا خلق قيمة معاً.',
    'contact.schedule': 'جدولة اجتماع',
    'contact.download': 'تحميل ملف المستثمرين',
    'contact.description': 'مستعد لاستكشاف الشراكات أو مناقشة الفرص أو معرفة المزيد عن حلولنا التقنية؟ نحن هنا لمساعدتك على النجاح.',
    'contact.cairo': 'القاهرة',
    'contact.egypt': 'مصر',
    'contact.newCairo': 'منطقة القاهرة الجديدة التجارية',
    
    // Footer
    'footer.allRightsReserved': 'جميع الحقوق محفوظة.',
    'footer.about': 'نبذة عن BHG',
    'footer.portfolio': 'المحفظة',
    'footer.services': 'الخدمات',
    'footer.contact': 'اتصل بنا',
    'footer.quickLinks': 'روابط سريعة',
    'footer.ourServices': 'خدماتنا',
    'footer.hotelDevelopment': 'تطوير الفنادق',
    'footer.propertyManagement': 'إدارة العقارات',
    'footer.servicedApartments': 'شقق مخدومة',
    'footer.technologySolutions': 'حلول تقنية',
    'footer.boutiqueHotels': 'فنادق بوتيكية',
    'footer.ourPresence': 'تواجدنا',
    'footer.description': 'مجموعة ضيافة رائدة تعمل في مصر والمنطقة، رائدة في أماكن الإقامة البوتيكية والحلول التقنية المملوكة.',
    'footer.headquarters': 'المقر الرئيسي: الزمالك، مصر',
    'footer.marketShare': '32% حصة السوق | 500+ وحدة | 5+ مدن',
    'footer.locations.cairo': 'القاهرة، مصر',
    'footer.locations.alexandria': 'الإسكندرية، مصر',
    'footer.locations.northCoast': 'الساحل الشمالي، مصر',
    'footer.locations.hurghada': 'الغردقة، مصر',
    'footer.locations.jeddah': 'جدة، السعودية',
    
    // Careers
    'careers.title': 'انضم إلى فريقنا',
    'careers.subtitle': 'ابن مسيرتك المهنية مع مجموعة براسبيل للضيافة',
    'careers.description': 'نبحث دائماً عن الأفراد المواهب الذين يشاركوننا شغفنا بالتميز في الضيافة. انضم إلينا في تشكيل مستقبل الضيافة عبر منطقة الشرق الأوسط وشمال أفريقيا.',
    'careers.apply': 'تقدم الآن',
    'careers.viewPosition': 'عرض المنصب',
    'careers.noOpenings': 'لا توجد وظائف شاغرة حالياً',
    'careers.form.name': 'الاسم الكامل',
    'careers.form.email': 'عنوان البريد الإلكتروني',
    'careers.form.phone': 'رقم الهاتف',
    'careers.form.position': 'المنصب المتقدم له',
    'careers.form.experience': 'سنوات الخبرة',
    'careers.form.cover': 'خطاب التغطية',
    'careers.form.resume': 'رفع السيرة الذاتية',
    'careers.form.submit': 'تقديم الطلب',
    
    // Procurement
    'procurement.title': 'المشتريات',
    'procurement.subtitle': 'شارك معنا من أجل التميز',
    'procurement.description': 'نحن ملتزمون ببناء شراكات قوية مع الموردين الذين يشاركوننا قيم الجودة والموثوقية والابتكار.',
    'procurement.register': 'التسجيل كمورد',
    'procurement.requirements': 'متطلبات المورد',
    'procurement.process': 'عملية المشتريات',
    'procurement.contact': 'جهة اتصال المشتريات',
    
    // Homes
    'homes.title': 'الشقق المخدومة',
    'homes.subtitle': 'تجارب معيشة فاخرة',
    'homes.description': 'تجمع شققنا المخدومة بين راحة المنزل وفخامة خدمات الفنادق، مما يوفر حلول معيشة مرنة للإقامات الطويلة.',
    'homes.features': 'المميزات',
    'homes.locations': 'المواقع',
    'homes.bookNow': 'احجز الآن',
    
    // Hotels
    'hotels.title': 'الفنادق البوتيكية',
    'hotels.subtitle': 'تجارب ضيافة استثنائية',
    'hotels.description': 'تقدم فنادقنا البوتيكية تجارب فريدة مستوحاة محلياً مع وسائل الراحة ذات المستوى العالمي والخدمة الشخصية.',
    'hotels.amenities': 'وسائل الراحة',
    'hotels.rooms': 'الغرف والأجنحة',
    'hotels.dining': 'تناول الطعام',
    'hotels.bookNow': 'احجز الآن',
    
    // Developments
    'developments.title': 'التطوير العقاري',
    'developments.subtitle': 'بناء مستقبل الضيافة',
    'developments.description': 'تخلق مشاريعنا التطويرية مساحات مبتكرة تعيد تعريف الضيافة والمعيشة السكنية عبر منطقة الشرق الأوسط وشمال أفريقيا.',
    'developments.projects': 'المشاريع الحالية',
    'developments.completed': 'المشاريع المكتملة',
    'developments.planned': 'المشاريع المخططة',
    
    // Technologies
    'technologies.title': 'حلول التكنولوجيا العقارية',
    'technologies.subtitle': 'الابتكار في تكنولوجيا الضيافة',
    'technologies.description': 'نطور تقنيات إدارة العقارات وتجربة الضيوف المتطورة التي تدفع الكفاءة وتعزز الرضا.',
    'technologies.solutions': 'حلولنا',
    'technologies.features': 'الميزات الرئيسية',
    'technologies.integration': 'تكامل الأنظمة',
    
    // Job Application Form
    'job.title': 'تقديم طلب وظيفة',
    'job.description': 'نحن نبحث دائماً عن الأفراد الموهوبين للانضمام إلى فريقنا. يرجى ملء النموذج أدناه.',
    'job.firstName': 'الاسم الأول',
    'job.lastName': 'اسم العائلة',
    'job.email': 'عنوان البريد الإلكتروني',
    'job.phone': 'رقم الهاتف',
    'job.position': 'المنصب المهتم به',
    'job.experience': 'سنوات الخبرة',
    'job.resume': 'السيرة الذاتية',
    'job.coverLetter': 'خطاب التغطية',
    'job.cancel': 'إلغاء',
    'job.submit': 'تقديم الطلب',
    'job.submitting': 'جار التقديم...',
    'job.placeholder.firstName': 'أدخل اسمك الأول',
    'job.placeholder.lastName': 'أدخل اسم العائلة',
    'job.placeholder.email': 'أدخل عنوان بريدك الإلكتروني',
    'job.placeholder.phone': 'أدخل رقم هاتفك',
    'job.placeholder.position': 'اختر منصباً أو قسماً',
    'job.placeholder.experience': 'اختر مستوى خبرتك',
    'job.placeholder.coverLetter': 'أخبرنا لماذا تود الانضمام لفريقنا...',
    'job.positions.operations': 'العمليات',
    'job.positions.revenue': 'إدارة الإيرادات',
    'job.positions.guestServices': 'خدمات الضيوف',
    'job.positions.housekeeping': 'التدبير المنزلي',
    'job.positions.maintenance': 'الصيانة',
    'job.positions.foodBeverage': 'الطعام والمشروبات',
    'job.positions.management': 'الإدارة',
    'job.positions.other': 'أخرى',
    'job.experience.0-1': '0-1 سنوات',
    'job.experience.2-5': '2-5 سنوات',
    'job.experience.6-10': '6-10 سنوات',
    'job.experience.10+': '10+ سنوات',
    'job.fileFormats': 'التنسيقات المقبولة: PDF، DOC، DOCX (حد أقصى 5 ميجابايت)',
    'job.errors.firstName': 'الاسم الأول مطلوب',
    'job.errors.lastName': 'اسم العائلة مطلوب',
    'job.errors.email': 'البريد الإلكتروني مطلوب',
    'job.errors.emailInvalid': 'يرجى إدخال عنوان بريد إلكتروني صحيح',
    'job.errors.resume': 'يرجى رفع سيرتك الذاتية',
    'job.errors.experience': 'يرجى اختيار مستوى خبرتك',
    'job.success': 'تم تقديم الطلب بنجاح! سنتواصل معك قريباً.',
    'job.error': 'فشل في تقديم الطلب. يرجى المحاولة مرة أخرى.',

    // Common
    'common.learnMore': 'اعرف المزيد',
    'common.viewAll': 'عرض الكل',
    'common.backToHome': 'العودة إلى الرئيسية',
    'common.loading': 'جار التحميل...',
    'common.error': 'خطأ',
    'common.success': 'نجح',
    'common.close': 'إغلاق',
    'common.save': 'حفظ',
    'common.cancel': 'إلغاء',
    'common.next': 'التالي',
    'common.previous': 'السابق',
    'common.download': 'تحميل',
    'common.upload': 'رفع',
    'common.search': 'بحث',
    'common.filter': 'تصفية',
    'common.sort': 'ترتيب',
    
    // 404 Page
    'notFound.title': '404',
    'notFound.message': 'عذراً! الصفحة غير موجودة',
    'notFound.back': 'العودة إلى الرئيسية',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const language = 'en';
  const setLanguage = () => {}; // No-op since we only support English

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      <div>
        {children}
      </div>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};